import { Color, InstancedMesh, Matrix4, MeshBasicNodeMaterial, NormalBlending, PlaneGeometry, Quaternion, Vector3 } from 'three/webgpu';
import { float, hash, instanceIndex, length, mx_noise_float, smoothstep, uv, vec3, vec4 } from 'three/tsl';
import { Config } from '@src/core/Config';

const tmpMat = new Matrix4();
const tmpPos = new Vector3();
const tmpQuat = new Quaternion();
const tmpScale = new Vector3();
const tmpColor = new Color();
const Z_AXIS = new Vector3(0, 0, 1);

/**
 * Persistent battle damage: goo splats and burn marks written into a ring buffer
 * of instances that sits just above the floor. Cleared only on room change.
 */
export class DecalPool {
  readonly mesh: InstancedMesh;
  private readonly capacity = Config.pools.decals;
  private next = 0;
  private live = 0;

  constructor() {
    const geometry = new PlaneGeometry(1, 1);
    const material = new MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.depthTest = false;
    material.blending = NormalBlending;

    // Irregular splat: radial falloff perturbed by per-instance noise.
    const p = uv().sub(0.5).mul(2);
    const seed = hash(instanceIndex).mul(40);
    const n = mx_noise_float(vec3(p.mul(2.2), seed)).mul(0.28);
    const d = length(p).add(n);
    const splat = smoothstep(0.95, 0.55, d);
    const speck = smoothstep(0.35, 0.05, length(p.mul(1.6).add(n.mul(3))));
    material.colorNode = vec4(1, 1, 1, 1);
    // Kept translucent so a few hundred kills stain the floor without hiding it.
    material.opacityNode = splat.mul(0.38).add(speck.mul(0.15)).mul(float(1));

    this.mesh = new InstancedMesh(geometry, material, this.capacity);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1;
    this.mesh.count = 0;
    tmpColor.setHex(0xffffff);
    for (let i = 0; i < this.capacity; i++) this.mesh.setColorAt(i, tmpColor);
  }

  add(x: number, y: number, tint: number, scale: number, rot: number): void {
    const i = this.next;
    this.next = (this.next + 1) % this.capacity;
    if (this.live < this.capacity) this.live++;
    tmpPos.set(x, y, 0.02);
    tmpQuat.setFromAxisAngle(Z_AXIS, rot);
    tmpScale.set(scale, scale * 0.85, 1);
    tmpMat.compose(tmpPos, tmpQuat, tmpScale);
    this.mesh.setMatrixAt(i, tmpMat);
    tmpColor.setHex(tint).multiplyScalar(0.6);
    this.mesh.setColorAt(i, tmpColor);
    this.mesh.count = this.live;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  clear(): void {
    this.next = 0;
    this.live = 0;
    this.mesh.count = 0;
  }
}
